import React, { useEffect, useState } from 'react';
import './AdminSystemHealth.css';
import { fmtTime } from '../../../utils/ruDates';

function StatusIndicator({ status }) {
    const statusClass = `status-indicator status-indicator--${status}`;
    const labels = {
        healthy: '✓ Здоров',
        warning: '⚠️ Внимание',
        critical: '🔴 Критично'
    };
    return <div className={statusClass}>{labels[status] || status}</div>;
}

function MetricCard({ label, value, unit = '', trend = null, alert = false }) {
    return (
        <div className={`metric-card ${alert ? 'metric-card--alert' : ''}`}>
            <div className="metric-card__label">{label}</div>
            <div className="metric-card__value">
                {value}
                {unit && <span className="metric-card__unit">{unit}</span>}
            </div>
            {trend && (
                <div className={`metric-card__trend trend--${trend}`}>
                    {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trend}
                </div>
            )}
        </div>
    );
}

function AdminSystemHealth({ request, showStatus }) {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const fetchHealth = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await request('/api/v1/admin/system-health', {});
            setHealth(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch system health');
            showStatus?.('error', 'Ошибка загрузки статуса системы');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHealth();

        // Auto-refresh every 30 seconds
        if (autoRefresh) {
            const interval = setInterval(fetchHealth, 30000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh]);

    if (loading && !health) {
        return (
            <div className="admin-system-health">
                <div className="loading">
                    <span className="spinner" aria-hidden="true" />
                    <span>Загрузка статуса системы...</span>
                </div>
            </div>
        );
    }

    if (error && !health) {
        return (
            <div className="admin-system-health">
                <div className="error-message">
                    <h3>Ошибка загрузки данных</h3>
                    <p>{error}</p>
                    <button onClick={fetchHealth} className="btn-primary">Повторить</button>
                </div>
            </div>
        );
    }

    const metrics = health?.metrics || {};

    return (
        <div className="admin-system-health">
            <div className="system-health__header">
                <div>
                    <h2>System Health Dashboard</h2>
                    <p className="muted">
                        Обновлено: {fmtTime(health?.timestamp)}
                    </p>
                </div>
                <div className="system-health__controls">
                    <label className="checkbox">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Автообновление (30с)
                    </label>
                    <button onClick={fetchHealth} className="btn-secondary">
                        🔄 Обновить сейчас
                    </button>
                </div>
            </div>

            {/* Overall Status */}
            <div className="system-health__section">
                <div className="section-title">Общий статус</div>
                <div className="overall-status">
                    <StatusIndicator status={health?.status} />
                    {health?.status === 'critical' && (
                        <p className="status-message">⚠️ Требуется немедленное внимание!</p>
                    )}
                    {health?.status === 'warning' && (
                        <p className="status-message">⚠️ Требуется внимание</p>
                    )}
                </div>
            </div>

            {/* CPU Metrics */}
            <div className="system-health__section">
                <div className="section-title">🖥️ Процессор</div>
                <div className="metrics-grid">
                    <MetricCard
                        label="Использование CPU"
                        value={metrics.cpu?.usage_percent || 0}
                        unit="%"
                        trend={metrics.cpu?.trend}
                        alert={metrics.cpu?.usage_percent > 80}
                    />
                    <MetricCard
                        label="Ядер"
                        value={metrics.cpu?.cores || 0}
                    />
                </div>
            </div>

            {/* Memory Metrics */}
            <div className="system-health__section">
                <div className="section-title">💾 Оперативная память</div>
                <div className="metrics-grid">
                    <MetricCard
                        label="Использование памяти"
                        value={metrics.memory?.usage_percent || 0}
                        unit="%"
                        alert={metrics.memory?.usage_percent > 85}
                    />
                    <MetricCard
                        label="Использовано"
                        value={metrics.memory?.used_mb || 0}
                        unit=" МБ"
                    />
                    <MetricCard
                        label="Всего"
                        value={metrics.memory?.total_mb || 0}
                        unit=" МБ"
                    />
                    <MetricCard
                        label="Доступно"
                        value={metrics.memory?.available_mb || 0}
                        unit=" МБ"
                    />
                </div>
            </div>

            {/* Disk Metrics */}
            <div className="system-health__section">
                <div className="section-title">💿 Дисковое пространство</div>
                <div className="metrics-grid">
                    <MetricCard
                        label="Использование диска"
                        value={metrics.disk?.usage_percent || 0}
                        unit="%"
                        alert={metrics.disk?.usage_percent > 80}
                    />
                    <MetricCard
                        label="Использовано"
                        value={metrics.disk?.used_gb || 0}
                        unit=" ГБ"
                    />
                    <MetricCard
                        label="Всего"
                        value={metrics.disk?.total_gb || 0}
                        unit=" ГБ"
                    />
                    <MetricCard
                        label="Свободно"
                        value={metrics.disk?.free_gb || 0}
                        unit=" ГБ"
                    />
                </div>
            </div>

            {/* Database Metrics */}
            <div className="system-health__section">
                <div className="section-title">🗄️ База данных</div>
                <div className="metrics-grid">
                    <MetricCard
                        label="Активные соединения"
                        value={metrics.database?.connections_active || 0}
                    />
                    <MetricCard
                        label="Свободные соединения"
                        value={metrics.database?.connections_idle || 0}
                    />
                    <MetricCard
                        label="Размер пула"
                        value={metrics.database?.connections_max || 0}
                    />
                    <MetricCard
                        label="Загрузка пула"
                        value={metrics.database?.usage_percent || 0}
                        unit="%"
                        alert={metrics.database?.usage_percent > 75}
                    />
                    <MetricCard
                        label="Статус"
                        value={metrics.database?.connection_pool || 'unknown'}
                    />
                </div>
            </div>

            {/* Legend */}
            <div className="system-health__legend">
                <div className="legend-item">
                    <span className="status-indicator status-indicator--healthy">✓</span>
                    Здоров: Все метрики в норме
                </div>
                <div className="legend-item">
                    <span className="status-indicator status-indicator--warning">⚠️</span>
                    Внимание: Один или более параметр выше порога 75%
                </div>
                <div className="legend-item">
                    <span className="status-indicator status-indicator--critical">🔴</span>
                    Критично: Один или более параметр выше порога 90%
                </div>
            </div>
        </div>
    );
}

export default AdminSystemHealth;
